import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

type ReviewStatus = "pending" | "approved" | "rejected";

interface ReviewRow {
  id: string;
  user_id: string;
  product_id: string;
  rating: number;
  title: string | null;
  comment: string | null;
  photo_urls: string[] | null;
  status: ReviewStatus;
  moderation_notes: string | null;
  created_at: string;
  moderated_at: string | null;
  product_name?: string;
  user_email?: string;
}

const BUCKET = "review-photos";
const SIGNED_TTL = 60 * 30;

export function ReviewsModeration({ lang }: { lang: "en" | "es" }) {
  const [tab, setTab] = useState<ReviewStatus>("pending");
  const [rows, setRows] = useState<ReviewRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [signed, setSigned] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});

  const tt = (es: string, en: string) => (lang === "es" ? es : en);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("reviews")
      .select("id,user_id,product_id,rating,title,comment,photo_urls,status,moderation_notes,created_at,moderated_at")
      .eq("status", tab)
      .order("created_at", { ascending: false })
      .limit(100);
    if (error) {
      console.error("[ReviewsModeration] load", error);
      setRows([]);
      setLoading(false);
      return;
    }
    const items = (data || []) as ReviewRow[];

    // Enrich with product names and user emails
    const productIds = Array.from(new Set(items.map((r) => r.product_id).filter(Boolean)));
    const userIds = Array.from(new Set(items.map((r) => r.user_id).filter(Boolean)));
    const [prodRes, profRes] = await Promise.all([
      productIds.length
        ? supabase.from("products").select("id,name").in("id", productIds)
        : Promise.resolve({ data: [] as any[] }),
      userIds.length
        ? supabase.from("profiles").select("user_id,email,first_name").in("user_id", userIds)
        : Promise.resolve({ data: [] as any[] }),
    ]);
    const prodMap = new Map<string, string>();
    (prodRes.data || []).forEach((p: any) => prodMap.set(p.id, p.name));
    const userMap = new Map<string, string>();
    (profRes.data || []).forEach((p: any) =>
      userMap.set(p.user_id, p.email || p.first_name || p.user_id?.slice(0, 8))
    );

    const enriched = items.map((r) => ({
      ...r,
      product_name: prodMap.get(r.product_id) || r.product_id?.slice(0, 8),
      user_email: userMap.get(r.user_id) || r.user_id?.slice(0, 8),
    }));
    setRows(enriched);
    setLoading(false);

    // Sign all photos
    const paths = Array.from(
      new Set(
        enriched.flatMap((r) => r.photo_urls || []).filter((v) => v && !v.startsWith("http"))
      )
    ).filter((p) => !signed[p]);
    if (paths.length) {
      const { data: sd } = await supabase.storage.from(BUCKET).createSignedUrls(paths, SIGNED_TTL);
      const next: Record<string, string> = {};
      (sd || []).forEach((s: any, i: number) => {
        if (s?.signedUrl) next[paths[i]] = s.signedUrl;
      });
      if (Object.keys(next).length) setSigned((prev) => ({ ...prev, ...next }));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const moderate = async (row: ReviewRow, next: ReviewStatus) => {
    setBusyId(row.id);
    const { data: { user } } = await supabase.auth.getUser();
    const { error } = await supabase
      .from("reviews")
      .update({
        status: next,
        moderation_notes: notes[row.id] ?? row.moderation_notes ?? null,
        moderated_at: new Date().toISOString(),
        moderated_by: user?.id ?? null,
      })
      .eq("id", row.id);
    setBusyId(null);
    if (error) {
      alert(error.message);
      return;
    }
    setRows((prev) => prev.filter((r) => r.id !== row.id));
  };

  const remove = async (row: ReviewRow) => {
    if (!confirm(tt("¿Eliminar esta reseña permanentemente?", "Delete this review permanently?"))) return;
    setBusyId(row.id);
    const { error } = await supabase.from("reviews").delete().eq("id", row.id);
    setBusyId(null);
    if (error) {
      alert(error.message);
      return;
    }
    setRows((prev) => prev.filter((r) => r.id !== row.id));
  };

  const counts = useMemo(() => ({ count: rows.length }), [rows]);

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
        {(["pending", "approved", "rejected"] as ReviewStatus[]).map((s) => (
          <button
            key={s}
            className={`atab ${tab === s ? "on" : ""}`}
            onClick={() => setTab(s)}
          >
            {s === "pending" && tt("⏳ Pendientes", "⏳ Pending")}
            {s === "approved" && tt("✓ Aprobadas", "✓ Approved")}
            {s === "rejected" && tt("✕ Rechazadas", "✕ Rejected")}
            {tab === s ? ` (${counts.count})` : ""}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <button className="btn bg bs" onClick={load} disabled={loading} style={{ fontSize: 11 }}>
          {loading ? "…" : tt("Refrescar", "Refresh")}
        </button>
      </div>

      {loading && (
        <div style={{ color: "var(--tx3)", fontSize: 12, padding: 20, textAlign: "center" }}>
          {tt("Cargando…", "Loading…")}
        </div>
      )}

      {!loading && rows.length === 0 && (
        <div style={{ color: "var(--tx3)", fontSize: 12, padding: 30, textAlign: "center", background: "var(--bg2)", border: "1px dashed var(--bd2)", borderRadius: "var(--r2)" }}>
          {tab === "pending"
            ? tt("No hay reseñas pendientes de moderación.", "No reviews pending moderation.")
            : tt("Sin resultados.", "No results.")}
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {rows.map((r) => (
          <div
            key={r.id}
            style={{
              background: "var(--bg2)",
              border: "1px solid var(--bd2)",
              borderRadius: "var(--r2)",
              padding: 12,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, flexWrap: "wrap" }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 11, color: "var(--tx3)" }}>
                  {new Date(r.created_at).toLocaleString()} · {r.user_email}
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, marginTop: 2 }}>
                  {"⭐".repeat(r.rating)} · {r.product_name}
                </div>
                {r.title && <div style={{ fontSize: 12, fontWeight: 600, marginTop: 4 }}>{r.title}</div>}
                {r.comment && (
                  <p style={{ fontSize: 12, color: "var(--tx2)", margin: "6px 0", whiteSpace: "pre-wrap" }}>
                    {r.comment}
                  </p>
                )}
                {Array.isArray(r.photo_urls) && r.photo_urls.length > 0 && (
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
                    {r.photo_urls.map((v, i) => {
                      const src = v.startsWith("http") ? v : signed[v];
                      if (!src) {
                        return (
                          <div key={i} style={{ width: 64, height: 64, background: "var(--bg)", border: "1px solid var(--bd2)", borderRadius: 6 }} />
                        );
                      }
                      return (
                        <a key={i} href={src} target="_blank" rel="noopener noreferrer">
                          <img
                            src={src}
                            alt={`photo ${i + 1}`}
                            style={{ width: 64, height: 64, objectFit: "cover", border: "1px solid var(--bd2)", borderRadius: 6 }}
                          />
                        </a>
                      );
                    })}
                  </div>
                )}
                {r.moderation_notes && (
                  <div style={{ fontSize: 11, color: "var(--tx3)", marginTop: 6, fontStyle: "italic" }}>
                    {tt("Nota previa:", "Prior note:")} {r.moderation_notes}
                  </div>
                )}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 180 }}>
                <input
                  placeholder={tt("Nota interna (opcional)", "Internal note (optional)")}
                  value={notes[r.id] ?? ""}
                  onChange={(e) => setNotes((prev) => ({ ...prev, [r.id]: e.target.value }))}
                  style={{
                    fontSize: 11, padding: "5px 8px", border: "1px solid var(--bd2)",
                    borderRadius: 6, background: "var(--bg)", color: "var(--tx)", fontFamily: "var(--f)",
                  }}
                />
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {r.status !== "approved" && (
                    <button
                      className="btn bp bs"
                      style={{ fontSize: 11, flex: 1 }}
                      disabled={busyId === r.id}
                      onClick={() => moderate(r, "approved")}
                    >
                      ✓ {tt("Aprobar", "Approve")}
                    </button>
                  )}
                  {r.status !== "rejected" && (
                    <button
                      className="btn bg bs"
                      style={{ fontSize: 11, flex: 1 }}
                      disabled={busyId === r.id}
                      onClick={() => moderate(r, "rejected")}
                    >
                      ✕ {tt("Rechazar", "Reject")}
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => remove(r)}
                  disabled={busyId === r.id}
                  style={{ background: "none", border: "none", color: "var(--rd, #ef4444)", fontSize: 10, cursor: "pointer", textDecoration: "underline" }}
                >
                  🗑️ {tt("Eliminar", "Delete")}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}